import { FC, useMemo } from 'react'

import { Premise } from '@/api/models/auth/lib/auth.types'
import { RequestByIdApiType } from '@/api/models/requests'
import {
  RequestStatusAlias,
  RequestTypeAlias
} from '@/api/models/requests/lib/requests.types'

import { permanentTypeAliases } from '../../../model/constants'
import { ContentBlockAuthorTitle } from './ContentBlockAuthorTitle'
import ContentBlockAutos from './ContentBlockAutos'
import ContentBlockDateRange from './ContentBlockDateRange'
import ContentBlockDateStart from './ContentBlockDateStart'
import ContentBlockDescription from './ContentBlockDescription'
import ContentBlockFiles from './ContentBlockFiles'
import { ContentBlockPersonalAccount } from './ContentBlockPersonalAccount'
import ContentBlockPersons from './ContentBlockPersons'
import ContentBlockPremises from './ContentBlockPremises'
import ContentBlockProperties from './ContentBlockProperties'
import ContentBlockServiceName from './ContentBlockServiceName'
import { ContentRequestRaitingResult } from './ContentRequestRaitingResult'
import { getDisplayRequestDateEnd } from './lib/utils'

type Props = {
  request: RequestByIdApiType
  premises?: Premise[]
}

const ContentDefault: FC<Props> = ({ request, premises }) => {
  const isPermanentTypeAlias = useMemo(
    () => permanentTypeAliases.has(request.typeAlias),
    [request.typeAlias]
  )

  const filesToShow = useMemo(() => {
    if (request.rating) {
      return [...request.files, ...request.rating.files]
    }
    return request.files
  }, [request.files, request.rating])

  const isRatingEditorVisible =
    !request.rating &&
    request.typeAlias === RequestTypeAlias.CommonRequest &&
    [RequestStatusAlias.Complete].includes(request.statusAlias)

  return (
    <div className="flex flex-col">
      <div className="flex justify-between sm:flex-col">
        {request.serviceName && (
          <ContentBlockServiceName
            isPayable={request.isPayable}
            serviceName={request.serviceName}
            typeAlias={request.typeAlias}
          />
        )}
      </div>

      <div className="flex flex-col gap-24">
        <ContentBlockPremises
          premises={premises}
          currentPremiseId={request.premiseId}
        />
        {request.desiredDate && (
          <ContentBlockDateStart desiredDate={request.desiredDate} />
        )}
        {request.desiredDateStart && request.desiredDateEnd && (
          <ContentBlockDateRange
            dateStart={request.desiredDateStart}
            dateEnd={getDisplayRequestDateEnd(
              request.typeAlias,
              request.desiredDateEnd
            )}
          />
        )}
        <ContentBlockPersonalAccount
          personalAccountId={request.personalAccountId}
        />
        {isPermanentTypeAlias && Boolean(request.description) && (
          <ContentBlockDescription description={request.description ?? ''} />
        )}
        {request.persons && request.persons.length > 0 && (
          <ContentBlockPersons
            persons={request.persons}
            isForGuests={request.typeAlias === RequestTypeAlias.GuestPass}
          />
        )}
        {request.autos && request.autos.length > 0 && (
          <ContentBlockAutos
            premises={premises}
            autos={request.autos}
            canAutoBeChanged={request.canAutoBeChanged}
            anyParkingPlace={
              request.typeAlias === RequestTypeAlias.RentPassAuto
            }
          />
        )}
        {!isPermanentTypeAlias &&
          (Boolean(request.description) || Boolean(request.properties)) && (
            <ContentBlockProperties
              typeAlias={request.typeAlias}
              description={request.description}
              properties={request.properties}
            />
          )}
        <ContentBlockAuthorTitle authorTitle={request.authorTitle} />

        {!isRatingEditorVisible && request.rating && (
          <ContentRequestRaitingResult rating={request.rating} />
        )}

        <ContentBlockFiles files={filesToShow} />
      </div>
    </div>
  )
}

export default ContentDefault
